import * as React from 'react';
import { Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Separator } from '@/components/ui/Separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/Sheet';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import type { QFilterSection, QFilterDef } from './types';

interface QFilterPanelProps {
  sections: QFilterSection[];
  values: Record<string, unknown>;
  onApply: (values: Record<string, unknown>) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function QFilterPanel({ sections, values, onApply, open, onOpenChange }: QFilterPanelProps) {
  const [draft, setDraft] = React.useState<Record<string, unknown>>({});

  React.useEffect(() => {
    if (open) {
      setDraft({ ...values });
    }
  }, [open, values]);

  const activeCount = React.useMemo(() => {
    return Object.values(draft).filter((v) => {
      if (v === null || v === undefined || v === '') return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'object') {
        const obj = v as Record<string, unknown>;
        return obj.from || obj.to;
      }
      return true;
    }).length;
  }, [draft]);

  const setDraftValue = (key: string, value: unknown) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    onApply(draft);
    onOpenChange(false);
  };

  const handleClearAll = () => {
    onApply({});
    onOpenChange(false);
  };

  const sectionActiveCount = (section: QFilterSection): number => {
    return section.filters.filter((f) => {
      const v = draft[f.key];
      if (v === null || v === undefined || v === '') return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'object') {
        const obj = v as Record<string, unknown>;
        return obj.from || obj.to;
      }
      return true;
    }).length;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex flex-col w-full max-w-md">
        <SheetHeader>
          <SheetTitle>Filtros Avanzados</SheetTitle>
          <SheetDescription>Configura los filtros para refinar los resultados</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-4 space-y-6">
          {sections.map((section) => {
            const count = sectionActiveCount(section);
            return (
              <div key={section.key}>
                <div className="flex items-center gap-2 mb-3">
                  <h4 className="text-sm font-medium">{section.title}</h4>
                  {count > 0 && (
                    <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                      {count}
                    </Badge>
                  )}
                </div>
                <div className="space-y-3">
                  {section.filters.map((filter) => (
                    <FilterControl
                      key={filter.key}
                      filter={filter}
                      value={draft[filter.key]}
                      onChange={(v) => setDraftValue(filter.key, v)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <Separator />
        <SheetFooter className="flex-row justify-between pt-4">
          <Button variant="ghost" size="sm" onClick={handleClearAll}>
            <X className="h-3.5 w-3.5 mr-1" />
            Limpiar Todo
          </Button>
          <Button size="sm" onClick={handleApply}>
            <Filter className="h-3.5 w-3.5 mr-1" />
            Aplicar{activeCount > 0 && ` (${activeCount})`}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// --- Individual filter controls ---

interface FilterControlProps {
  filter: QFilterDef;
  value: unknown;
  onChange: (value: unknown) => void;
}

function FilterControl({ filter, value, onChange }: FilterControlProps) {
  switch (filter.type) {
    case 'select':
      return (
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">{filter.label}</label>
          <Select
            value={(value as string) ?? ''}
            onValueChange={(v) => onChange(v === '__none__' ? '' : v)}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder={filter.placeholder ?? 'Sin filtro'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Sin filtro</SelectItem>
              {filter.options?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case 'multi-select':
      return (
        <MultiSelectFilter filter={filter} value={value} onChange={onChange} />
      );

    case 'text':
      return (
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">{filter.label}</label>
          <Input
            className="h-9"
            placeholder={filter.placeholder ?? `Buscar ${filter.label.toLowerCase()}...`}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );

    case 'date-range':
      return <DateRangeFilter filter={filter} value={value} onChange={onChange} />;

    default:
      return null;
  }
}

function MultiSelectFilter({ filter, value, onChange }: FilterControlProps) {
  const selected = Array.isArray(value) ? (value as string[]) : [];
  const options = filter.options ?? [];

  const toggle = (optValue: string) => {
    const next = selected.includes(optValue)
      ? selected.filter((v) => v !== optValue)
      : [...selected, optValue];
    onChange(next);
  };

  return (
    <div>
      <label className="text-xs text-muted-foreground mb-1.5 block">{filter.label}</label>
      <div className={cn('space-y-1.5', options.length > 8 && 'max-h-48 overflow-y-auto')}>
        {options.map((opt) => (
          <label
            key={opt.value}
            className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5"
          >
            <Checkbox
              checked={selected.includes(opt.value)}
              onCheckedChange={() => toggle(opt.value)}
            />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
}

function DateRangeFilter({ filter, value, onChange }: FilterControlProps) {
  const range = (value && typeof value === 'object' && !Array.isArray(value))
    ? (value as { from?: string; to?: string })
    : {};

  const update = (field: 'from' | 'to', v: string) => {
    onChange({ ...range, [field]: v || undefined });
  };

  return (
    <div>
      <label className="text-xs text-muted-foreground mb-1 block">{filter.label}</label>
      <div className="flex items-center gap-2">
        <Input
          type="date"
          className="h-9 flex-1"
          value={range.from ?? ''}
          onChange={(e) => update('from', e.target.value)}
        />
        <span className="text-xs text-muted-foreground">a</span>
        <Input
          type="date"
          className="h-9 flex-1"
          value={range.to ?? ''}
          onChange={(e) => update('to', e.target.value)}
        />
      </div>
    </div>
  );
}

export { QFilterPanel };
